import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2, Bell, BellOff, ChevronRight, Bookmark } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SavedSearchesPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useApp();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    fetchSearches();
  }, [isAuthenticated, user]);

  const fetchSearches = async () => {
    try {
      const res = await fetch(`${API_URL}/api/saved-searches/${user.id}`);
      if (res.ok) {
        const data = await res.json();
        setSearches(data);
      }
    } catch (e) {
      console.error('Failed to fetch saved searches:', e);
    } finally {
      setLoading(false);
    }
  };

  const deleteSearch = async (searchId) => {
    try {
      const res = await fetch(`${API_URL}/api/saved-searches/${searchId}`, { method: 'DELETE' });
      if (res.ok) {
        setSearches(searches.filter(s => s.id !== searchId));
        toast.success('Search removed');
      }
    } catch {
      toast.error('Failed to remove search');
    }
  };

  const clearMatches = async (searchId) => {
    try {
      await fetch(`${API_URL}/api/saved-searches/${searchId}/clear`, { method: 'PUT' });
      setSearches(searches.map(s => s.id === searchId ? { ...s, new_matches: 0 } : s));
    } catch {}
  };

  const applySearch = (search) => {
    const f = search.filters || {};
    const params = new URLSearchParams();
    if (f.query) params.set('q', f.query);
    if (f.category && f.category !== 'all') params.set('category', f.category);
    clearMatches(search.id);
    navigate(`/browse?${params.toString()}`);
  };

  if (!isAuthenticated) {
    return (
      <Layout title="Saved Searches" showBack>
        <div className="p-8 text-center">
          <Bookmark className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-500 mb-4">Sign in to save searches</p>
          <Button onClick={() => navigate('/login')} className="bg-minted-800 hover:bg-minted-700 text-white">
            Sign In
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Saved Searches" showBack>
      <div className="p-4" data-testid="saved-searches-page">
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-20 bg-stone-100 rounded-lg animate-pulse" />)}
          </div>
        ) : searches.length === 0 ? (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="font-display text-lg text-stone-700 mb-2">No Saved Searches</h3>
            <p className="text-stone-400 text-sm mb-6">
              Browse products and tap "Save This Search" to get notified when new items match your filters.
            </p>
            <Button onClick={() => navigate('/browse')} className="bg-minted-800 hover:bg-minted-700 text-white">
              Browse Products
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-stone-400 uppercase tracking-wider mb-2">
              {searches.length} saved search{searches.length !== 1 ? 'es' : ''}
            </p>
            {searches.map(search => {
              const f = search.filters || {};
              const tags = [];
              if (f.category && f.category !== 'all') tags.push(f.category);
              if (f.condition && f.condition !== 'all') tags.push(f.condition);
              if (f.price_max < 999999) tags.push(`Under $${f.price_max}`);
              if (f.shipping_available) tags.push('Ships');

              return (
                <div
                  key={search.id}
                  className="bg-white border border-stone-100 rounded-lg p-4 flex items-start gap-3"
                  data-testid={`saved-search-${search.id}`}
                >
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => applySearch(search)}>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-stone-900 text-sm truncate">{search.name}</h3>
                      {search.new_matches > 0 && (
                        <Badge className="bg-minted-100 text-minted-800 text-[10px] border-0 rounded-full px-1.5">
                          {search.new_matches} new
                        </Badge>
                      )}
                    </div>
                    {f.query && (
                      <p className="text-xs text-stone-400 mb-1">Search: "{f.query}"</p>
                    )}
                    {tags.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {tags.map((tag, i) => (
                          <span key={i} className="text-[10px] bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-1.5 text-[10px] text-stone-400">
                      {search.notify ? (
                        <><Bell className="w-3 h-3" /> Notifications on</>
                      ) : (
                        <><BellOff className="w-3 h-3" /> Notifications off</>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => applySearch(search)}
                      className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 text-stone-400" />
                    </button>
                    <button
                      onClick={() => deleteSearch(search.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid={`delete-search-${search.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-stone-300 hover:text-red-500" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SavedSearchesPage;
